import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  type LayoutChangeEvent,
  type NativeSyntheticEvent,
  type TextLayoutEventData,
  Text as RNText,
  View,
} from "@-/com.npmjs.react-native";
import { useCallback, useEffect, useMemo, useState } from "react";
import { hasMinLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

export type MorphemeDetailsTilePinyinSyllable = {
  color: string | null;
  pinyin: string;
};

export type MorphemeDetailsTilePinyinEntry = {
  color: string | null;
  pinyin: string;
  /** Inline syllables for one reading (e.g. `xué` + `xí` without a comma separator). */
  syllables?: readonly MorphemeDetailsTilePinyinSyllable[];
  /** Tone-number form for this reading (e.g. `gan4`), used to match the glyph's primary pronunciation. */
  toneNumbersPinyin?: string;
};

function sortPinyinEntriesWithPrimaryFirst(
  entries: readonly MorphemeDetailsTilePinyinEntry[],
  primaryToneNumbersPinyin: string | null | undefined,
): readonly MorphemeDetailsTilePinyinEntry[] {
  if (
    primaryToneNumbersPinyin == null ||
    primaryToneNumbersPinyin.length === 0 ||
    !hasMinLength(entries, 2)
  ) {
    return entries;
  }
  const primaryIndex = entries.findIndex(
    (entry) => entry.toneNumbersPinyin === primaryToneNumbersPinyin,
  );
  if (primaryIndex <= 0) {
    return entries;
  }
  const primaryEntry = entries[primaryIndex];
  if (primaryEntry === undefined) {
    return entries;
  }
  return [primaryEntry, ...entries.slice(0, primaryIndex), ...entries.slice(primaryIndex + 1)];
}

export function MorphemeDetailsTilePinyinRow({
  entries = [],
  fontSize,
  lineHeight,
  primaryToneNumbersPinyin = null,
}: {
  entries?: readonly MorphemeDetailsTilePinyinEntry[];
  fontSize: number;
  lineHeight: number;
  /** Reading that colors the glyph; shown first so it matches the glyph tone color. */
  primaryToneNumbersPinyin?: string | null;
}) {
  const sortedEntries = useMemo(
    () => sortPinyinEntriesWithPrimaryFirst(entries, primaryToneNumbersPinyin),
    [entries, primaryToneNumbersPinyin],
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const [fitCount, setFitCount] = useState(arrayLength(sortedEntries));

  useEffect(() => {
    setFitCount(arrayLength(sortedEntries));
  }, [sortedEntries, containerWidth]);

  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const handleTextLayout = useCallback(
    (event: NativeSyntheticEvent<TextLayoutEventData>) => {
      if (containerWidth <= 0) {
        return;
      }
      const { lines } = event.nativeEvent;
      const firstLine = lines[0];
      const overflowed =
        hasMinLength(lines, 2) ||
        (firstLine !== undefined && firstLine.width > containerWidth + 0.5);
      if (overflowed && fitCount > 1) {
        setFitCount((current) => Math.max(1, current - 1));
      }
    },
    [containerWidth, fitCount],
  );

  const totalCount = arrayLength(sortedEntries);
  const hasPinyin =
    totalCount > 0 && sortedEntries[0] !== undefined && sortedEntries[0].pinyin.length > 0;
  const safeFitCount = Math.min(Math.max(1, fitCount), totalCount);
  const visible = hasPinyin ? sortedEntries.slice(0, safeFitCount) : [];
  const hiddenCount = hasPinyin ? Math.max(0, totalCount - safeFitCount) : 0;

  return (
    <View className="min-h-0 min-w-0 flex-1 self-stretch" onLayout={handleContainerLayout}>
      {hasPinyin ? (
        <Text
          className="text-muted-foreground"
          ellipsizeMode="clip"
          numberOfLines={1}
          onTextLayout={handleTextLayout}
          style={{ fontSize, lineHeight }}
        >
          {visible.flatMap((entry, index) => {
            const segmentsToRender =
              entry.syllables != null && isNonEmpty(entry.syllables)
                ? entry.syllables
                : [{ color: entry.color, pinyin: entry.pinyin }];
            const segments = [];
            if (index > 0) {
              // Comma outside the tone-colored segment so it keeps muted-foreground.
              segments.push(
                <RNText key={`sep-${index}`} style={{ fontSize, lineHeight }}>
                  ,{" "}
                </RNText>,
              );
            }
            for (const [segmentIndex, segment] of segmentsToRender.entries()) {
              const segmentStyle =
                segment.color !== null
                  ? { color: segment.color, fontSize, lineHeight }
                  : { fontSize, lineHeight };
              segments.push(
                // Nested RNText (not the design-system Text) so we don't reapply
                // `text-base` / `leading-normal` per segment, which would overpower
                // the dynamic fontSize / lineHeight set on the parent line.
                <RNText key={`${entry.pinyin}-${index}-${segmentIndex}`} style={segmentStyle}>
                  {segment.pinyin}
                </RNText>,
              );
            }
            return segments;
          })}
          {hiddenCount > 0 ? `, +${hiddenCount}` : null}
        </Text>
      ) : (
        <Text
          className="text-muted-foreground"
          ellipsizeMode="clip"
          numberOfLines={1}
          style={{ fontSize, lineHeight }}
        >
          -
        </Text>
      )}
    </View>
  );
}
